import React, { useEffect, useState } from "react";
import Select from "../../../form/select/Select";
import useForm from "../../../../hooks/useForm";
import Button from "../../../form/Button";
import {
  pageAddEdit,
  pageDetails,
  postAddEdit,
  postDetails,
} from "../../../../services/postService";
import { useMutation, useQuery } from "@tanstack/react-query";
import Input from "../../../form/Input";
import { toast } from "react-toastify";
import { taxonomyList } from "../../../../services/taxonomyService";
import Editor from "../../../elements/tiptap-editor/Editor";
import DatePicker from "../../../form/DatePicker";
import Textarea from "../../../form/Textarea";
import MultiselectDropdown from "../../../form/MultiselectDropdown";
import Tags from "../../../form/Tags";
import { useNavigate, useParams } from "react-router-dom";
import SelectedMediaCard from "../../../elements/SelectedMediaCard";
import SelectImageGallery from "../../../form/SelectImageGallery";
import SelectSingleImage from "../../../form/SelectSingleImage";
import FlexibleSelect from "../../../form/select/FlexibleSelect";
import LanguageTabs from "../../../elements/LanguageSwitcher";
import {
  generateSlug,
  supportedLanguages,
  userLang,
} from "../../../../helpers";

const validation = {
  // title: { required: true, message: "title is required" },
  // content: { required: true, message: "content is required" },
};

const ManagePage = ({ getResponse = () => {}, close = () => {} }) => {
  const params = useParams();
  const navigate = useNavigate();
  const id = params?.id;
  const [tags, setTags] = useState([]);
  const [selectedMedia, setSelectedMedia] = useState([]);
  const [featuredImage, setFeaturedImage] = useState("");
  const {
    values,
    handleChange,
    handleSubmit,
    errors,
    setFieldsValue,
    resetField,
    formValidate,
  } = useForm({
    validationRules: validation,
  });
  const [categories, setCategories] = useState([]);
  const [organization, setOrganization] = useState(null);
  const [currentLang, setCurrentLang] = useState(userLang || "en");
  const handleLangChange = (lang) => {
    setCurrentLang(lang);
  };

  const { data: details } = useQuery({
    queryKey: ["post-details", id],
    queryFn: async () => {
      const res = await pageDetails({ id: id });
      return res?.data;
    },
    enabled: !!id,
  });

  useEffect(() => {
    if (details?._id) {
      setFieldsValue({
        ...details,
        content: {
          ...supportedLanguages.reduce((acc, lang) => {
            acc[lang.code] = {
              title: details?.content?.[lang.code]?.title || "",
              content: details?.content?.[lang.code]?.content || "",
              excerpt: details?.content?.[lang.code]?.excerpt || "",
              seo: details?.content?.[lang.code]?.seo || {},
            };
            return acc;
          }, {}),
        },
      });
      setOrganization(details?.organization);
      setCategories(details?.categories);
      setFeaturedImage(details?.coverMedia);
      setSelectedMedia(details?.mediaGallery);
    }
  }, [details]);

  const {
    mutate,
    error,
    isPending: isLoading,
  } = useMutation({
    mutationFn: async (val) => {
      const media = selectedMedia?.reduce((acc, item, index) => {
        acc[`mediaGallery[${index}]`] = item?._id;
        return acc;
      }, {});

      const tag = tags?.reduce((acc, tag, index) => {
        acc[`tags[${index}]`] = tag;
        return acc;
      }, {});

      // const categories = val?.categories?.reduce((acc, category, index) => {
      //   acc[`categories[${index}]`] = category;
      //   return acc;
      // }, {});

      return await pageAddEdit({
        title: val?.title,
        content: val?.content,
        status: val?.status,
        excerpt: val?.excerpt,
        slug: val?.slug,
        // lang: localStorage.getItem("lang"),
        // publishDate: val?.publishDate,
        type: "page",
        organization: organization,
        // coverMedia: featuredImage?._id,
        // categories: categories,
        ...(details?._id && { id: details._id }),
        // ...media,
        // ...tag,
        // ...categories,
      });
    },

    onSuccess: (res) => {
      getResponse(res);
      if (res?.status === 200 || res?.status === 201) {
        navigate(`/admin/page`);
      }
      return res;
    },
    onError: (error) => {
      console.log(error);
    },
  });
  const { data: categoryData } = useQuery({
    queryKey: ["category"],
    queryFn: async () => {
      const res = await taxonomyList({
        limit: 10000,
        type: "post-category",
      });
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.docs;
    },
  });

  const { data: tagsData } = useQuery({
    queryKey: ["tags"],
    queryFn: async () => {
      const res = await taxonomyList({
        limit: 10000,
        type: "post-tag",
      });
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.docs;
    },
  });

  return (
    <>
      <form onSubmit={(event) => handleSubmit(event, mutate)}>
        <div className="flex justify-between items-center w-full px-6">
          <div className="font-normal text-lg xl:text-3xl text-primary">
            Manage Page
          </div>
          {/* <LanguageTabs
            languages={supportedLanguages}
            value={currentLang}
            onChange={handleLangChange}
          /> */}
          <div className="flex gap-4">
            <Button
              buttonType="submit"
              buttonLabel="Save Page"
              buttonClasses="min-w-28"
              buttonLabelClasses="mx-auto"
              {...(isLoading && {
                loading: true,
                buttonIconPosition: "left",
                isDisable: true,
              })}
            />
            <Button
              buttonLabel="Cancel"
              buttonClasses="min-w-28 !bg-white !text-primary border !border-primary"
              buttonLabelClasses="mx-auto"
              buttonHasLink={true}
              buttonLink={"/admin/Page"}
            />
          </div>
        </div>

        <div className="grid grid-cols-4 gap-6 p-6">
          <div className="col-span-3 space-y-4">
            <Input
              label="Title"
              inputPlaceholder="Enter title"
              required={false}
              inputName={`content.${currentLang}.title`}
              value={values?.content?.[currentLang]?.title || ""}
              onChange={(e) => {
                const newValue = e.target.value;
                setFieldsValue({
                  ...values,
                  content: {
                    ...values.content,
                    [currentLang]: {
                      ...values.content?.[currentLang],
                      title: newValue,
                    },
                  },
                });
              }}
            />

            <Editor
              label={"Content"}
              height={200}
              value={values?.content?.[currentLang]?.content || ""}
              placeholder={"Write Something"}
              onChange={(val) => {
                setFieldsValue({
                  ...values,
                  content: {
                    ...values.content,
                    [currentLang]: {
                      ...values.content?.[currentLang],
                      content: val,
                    },
                  },
                });
              }}
            />
            {/* <Textarea
              label="Excerpt"
              inputPlaceholder="Write excerpt"
              inputClasses="!h-28"
              inputName="excerpt"
              inputValue={values?.content?.[currentLang]?.excerpt || ""}
              onChange={(e) => {
                const newValue = e.target.value;
                setFieldsValue({
                  ...values,
                  content: {
                    ...values.content,
                    [currentLang]: {
                      ...values.content?.[currentLang],
                      excerpt: newValue,
                    },
                  },
                });
              }}
            /> */}
            {/* <div className="bg-white p-4 rounded-md border border-text-secondary/50">
              <SelectImageGallery
                label="Choose Media"
                values={selectedMedia}
                onChange={(newMedia) => setSelectedMedia(newMedia)}
                isSingle={false}
              />
            </div> */}

            <div className="relative col-span-4 mt-5">
              <div className="flex gap-4">
                <Button
                  buttonType="submit"
                  buttonLabel={"Save Page"}
                  buttonClasses={"min-w-28"}
                  buttonLabelClasses={"mx-auto"}
                  {...(isLoading && {
                    loading: true,
                    buttonIconPosition: "left",
                    isDisable: true,
                  })}
                />
                <Button
                  buttonLabel={"Cancel"}
                  buttonClasses={
                    "min-w-28 !bg-white !text-primary border !border-primary"
                  }
                  buttonLabelClasses={"mx-auto"}
                  buttonHasLink={true}
                  buttonLink={"/admin/page"}
                />
              </div>
            </div>
          </div>

          <div className="col-span-1 space-y-6">
            {/* <div className="border border-gray-300 rounded-lg p-4 space-y-2">
              <SelectSingleImage
                label="Upload Featured Image"
                size="lg"
                value={featuredImage}
                onChange={(img) => setFeaturedImage(img)}
                onRemove={() => setFeaturedImage(null)}
              />
            </div> */}

            <div className="border border-gray-300 rounded-lg p-4 space-y-2">
              {/* <DatePicker
                label={"Publish Date"}
                placeholder={"Choose publish date"}
                position="fixed"
                onChange={(e) =>
                  handleChange({
                    target: {
                      name: "date",
                      value: e?.startDate || e?.endDate,
                    },
                  })
                }
                value={{
                  startDate: values?.date,
                  endDate: values?.date,
                }}
                {...formValidate(errors, "date")}
              /> */}
              <FlexibleSelect
                label={"Status"}
                placeholder={"Choose status"}
                onChange={(e) =>
                  handleChange({ target: { name: "status", value: e } })
                }
                selectedValue={values?.status}
                dropdownData={[
                  { name: "Active", value: "active" },
                  { name: "Inactive", value: "inactive" },
                ]}
                {...formValidate(errors, "status")}
              />
              {/* <FlexibleSelect
                label={"Select category"}
                isSearch={true}
                dropdownData={categoryData?.data?.map((item) => {
                  return {
                    name: item?.content?.[currentLang]?.title,
                    value: item?._id,
                  };
                })}
                selectedValue={categories}
                onChange={setCategories}
                type="multiple"
                placeholder="Choose categories"
              /> */}
              {/* <Tags
                label="Tags"
                placeholder="Enter tags"
                options={tagsData?.data?.map((item) => ({
                  label: item.content?.[currentLang]?.title,
                  _id: item._id,
                }))}
                selectedItems={tags}
                setSelectedItems={setTags}
                tagsType="input"
              /> */}
            </div>
          </div>
        </div>
      </form>
    </>
  );
};

export default ManagePage;
